import { Constructor } from "@/domains/common/types";
import { GenericSyncOperationKind } from "@/store/sync/types";
import { BaseSyncOperationGeneric } from "@/store/sync/operations/BaseSyncOperationGeneric";
import {
  ExternalOperationKind,
  IExternalOperation,
  ISyncOperation,
  ISyncOperationGuestMode,
} from "@/store/sync/operations/types";

import { CreateNoteOperation } from "@/store/sync/operations/notes/CreateNoteOperation";
import { DeleteNoteOperation } from "@/store/sync/operations/notes/DeleteNoteOperation";
import { TrashNoteOperation } from "@/store/sync/operations/notes/TrashNoteOperation";
import { RestoreNoteOperation } from "@/store/sync/operations/notes/RestoreNoteOperation";
import { GrantNoteAclViaSpaceAccountOperation } from "@/store/sync/operations/notes/GrantNoteAclViaSpaceAccountOperation";
import { GrantNoteAclViaEmailAddressOperation } from "@/store/sync/operations/notes/GrantNoteAclViaEmailAddressOperation";
import { UpdateNoteAclViaSpaceAccountOperation } from "@/store/sync/operations/notes/UpdateNoteAclViaSpaceAccountOperation";
import { UpdateNoteAclViaEmailAddressOperation } from "@/store/sync/operations/notes/UpdateNoteAclViaEmailAddressOperation";
import { RevokeNoteAclViaSpaceAccountOperation } from "@/store/sync/operations/notes/RevokeNoteAclViaSpaceAccountOperation";
import { RevokeNoteAclViaEmailAddressOperation } from "@/store/sync/operations/notes/RevokeNoteAclViaEmailAddressOperation";
import { UpdateNoteAclViaCollectionOperation } from "@/store/sync/operations/notes/UpdateNoteAclViaCollectionOperation";
import { CreateCollectionOperation } from "@/store/sync/operations/collections/CreateCollectionOperation";
import { UpdateCollectionOperation } from "@/store/sync/operations/collections/UpdateCollectionOperation";
import { DeleteCollectionOperation } from "@/store/sync/operations/collections/DeleteCollectionOperation";
import { AddNoteToCollectionOperation } from "@/store/sync/operations/collections/AddNoteToCollectionOperation";
import { RemoveNoteFromCollectionOperation } from "@/store/sync/operations/collections/RemoveNoteFromCollectionOperation";
import { GrantCollectionAclViaSpaceAccountOperation } from "@/store/sync/operations/collections/GrantCollectionAclViaSpaceAccountOperation";
import { GrantCollectionAclViaEmailAddressOperation } from "@/store/sync/operations/collections/GrantCollectionAclViaEmailAddressOperation";
import { UpdateCollectionAclViaSpaceAccountOperation } from "@/store/sync/operations/collections/UpdateCollectionAclViaSpaceAccountOperation";
import { UpdateCollectionAclViaEmailAddressOperation } from "@/store/sync/operations/collections/UpdateCollectionAclViaEmailAddressOperation";
import { RevokeCollectionAclViaSpaceAccountOperation } from "@/store/sync/operations/collections/RevokeCollectionAclViaSpaceAccountOperation";
import { RevokeCollectionAclViaEmailAddressOperation } from "@/store/sync/operations/collections/RevokeCollectionAclViaEmailAddressOperation";
import { AddNoteToFavoritesOperation } from "@/store/sync/operations/favorites/AddNoteToFavoritesOperation";
import { AddCollectionToFavoritesOperation } from "@/store/sync/operations/favorites/AddCollectionToFavoritesOperation";
import { AddSavedSearchToFavoritesOperation } from "@/store/sync/operations/favorites/AddSavedSearchToFavoritesOperation";
import { RemoveNoteFromFavoritesOperation } from "@/store/sync/operations/favorites/RemoveNoteFromFavoritesOperation";
import { RemoveCollectionFromFavoritesOperation } from "@/store/sync/operations/favorites/RemoveCollectionFromFavoritesOperation";
import { RemoveSavedSearchFromFavoritesOperation } from "@/store/sync/operations/favorites/RemoveSavedSearchFromFavoritesOperation";
import { UpdateFavoriteItemSortKeyOperation } from "@/store/sync/operations/favorites/UpdateFavoriteItemSortKeyOperation";
import { UpdateNoteContentUsingDiffOperation } from "@/store/sync/operations/notes/UpdateNoteContentUsingDiffOperation";
import { UpdateNoteContentOverwritingFullStateDestructivelyOperation } from "@/store/sync/operations/notes/UpdateNoteContentOverwritingFullStateDestructivelyOperation";
import { MarkNoteViewedOperation } from "@/store/sync/operations/recents/MarkNoteViewedOperation";
import { MarkCollectionViewedOperation } from "@/store/sync/operations/recents/MarkCollectionViewedOperation";
import { MakeCollectionPrivateOperation } from "@/store/sync/operations/collections/MakeCollectionPrivateOperation";
import { CreateChatConversationOperation } from "@/store/sync/operations/chat/CreateChatConversationOperation";
import { SubmitChatMessageOperation } from "@/store/sync/operations/chat/SubmitChatMessageOperation";
import { SaveDraftNoteOperation } from "@/store/sync/operations/chat/SaveDraftNoteOperation";
import { SaveChatMessageOperation } from "@/store/sync/operations/chat/SaveChatMessageOperation";
import { UploadImageExternalOperation } from "@/store/sync/operations/external/UploadImageExternalOperation";
import { UploadFileExternalOperation } from "@/store/sync/operations/external/UploadFileExternalOperation";
import { ProvideChatMessageFeedbackOperation } from "@/store/sync/operations/chat/ProvideChatMessageFeedbackOperation";
import { ProvideFeedbackOperation } from "@/store/sync/operations/feedback/ProvideFeedbackOperation";
import { SubmitGuidedChatMessageOperation } from "@/store/sync/operations/chat/SubmitGuidedChatMessageOperation";
import { DismissSpaceAccountAppCalloutOperation } from "@/store/sync/operations/app-callout/DismissSpaceAccountAppCalloutOperation";
import { CreateApiKeyOperation } from "@/store/sync/operations/api-keys/CreateApiKeyOperation";
import { CreateTemplateOperation } from "@/store/sync/operations/templates/CreateTemplateOperation";
import { UpdateTemplateOperation } from "@/store/sync/operations/templates/UpdateTemplateOperation";
import { RevokeTemplateAclViaSpaceAccountOperation } from "@/store/sync/operations/templates/RevokeTemplateAclViaSpaceAccountOperation";
import { DeleteTemplateOperation } from "@/store/sync/operations/templates/DeleteTemplateOperation";
import { GrantTemplateAclViaEmailAddressOperation } from "@/store/sync/operations/templates/GrantTemplateAclViaEmailAddressOperation";
import { GrantTemplateAclViaSpaceAccountOperation } from "@/store/sync/operations/templates/GrantTemplateAclViaSpaceAccountOperation";
import { UpdateTemplateContentUsingDiffOperation } from "@/store/sync/operations/templates/UpdateTemplateContentUsingDiffOperation";
import { CreateNoteUsingTemplateOperation } from "@/store/sync/operations/templates/CreateNoteUsingTemplateOperation";
import { UpdateNoteContentUsingTemplateAndDiffOperation } from "@/store/sync/operations/notes/UpdateNoteContentUsingTemplateAndDiffOperation";
import { AddTemplateToCollectionOperation } from "@/store/sync/operations/templates/AddTemplateToCollectionOperation";
import { RemoveTemplateFromCollectionOperation } from "@/store/sync/operations/templates/RemoveTemplateFromCollectionOperation";
import { UpdateTemplateAclViaCollectionOperation } from "@/store/sync/operations/templates/UpdateTemplateAclViaCollectionOperation";
import { UpdateTemplateAclViaSpaceAccountOperation } from "@/store/sync/operations/templates/UpdateTemplateAclViaSpaceAccountOperation";
import { MarkTemplateViewedOperation } from "@/store/sync/operations/templates/MarkTemplateViewedOperation";
import { MarkTemplateUsedOperation } from "@/store/sync/operations/templates/MarkTemplateUsedOperation";
import { CreateDataExportOperation } from "@/store/sync/operations/data-exports/CreateDataExportOperation";
import { CreateUploadedFileBatchOperation } from "@/store/sync/operations/uploaded-file/CreateUploadedFileBatchOperation";
import { CreateUploadedFileOperation } from "@/store/sync/operations/uploaded-file/CreateUploadedFileOperation";
import { StartProcessingUploadedFileBatchOperation } from "@/store/sync/operations/uploaded-file/StartProcessingUploadedFileBatchOperation";
import { UpdateApiKeyOperation } from "@/store/sync/operations/api-keys/UpdateApiKeyOperation";
import { DeleteApiKeyOperation } from "@/store/sync/operations/api-keys/DeleteApiKeyOperation";
import { CreateSessionOperation } from "@/store/sync/operations/sessions/CreateSessionOperation";
import { CreateSessionEventOperation } from "@/store/sync/operations/sessions/CreateSessionEventOperation";
import { BatchCreateUploadedFileOperation } from "@/store/sync/operations/uploaded-file/BatchCreateUploadedFileOperation";
import { UpdateSessionOperation } from "@/store/sync/operations/sessions/UpdateSessionOperation";
import { UpdateUploadedFileBatchOperation } from "@/store/sync/operations/uploaded-file/UpdateUploadedFileBatchOperation";
import { CreateMessageSentSessionEventOperation } from "@/store/sync/operations/events/CreateMessageSentEventOperation";
import { DeleteNoteSourceOperation } from "@/store/sync/operations/sources/DeleteNoteSourceOperation";
import { AcceptNoteSuggestionOperation } from "@/store/sync/operations/notes/AcceptNoteSuggestionOperation";
import { RejectNoteSuggestionOperation } from "@/store/sync/operations/notes/RejectNoteSuggestionOperation";

/**
 * A mapping of operation kinds to their respective operation classes.
 *
 * Note that this is marked as "Partial" - if we add a new operation to the backend,
 * we don't want to break the API typings here. (Callers should handle the case where
 * the operation kind is not found in this map.)
 */
export const getOperationKindToClassMap = (): Partial<
  Record<
    GenericSyncOperationKind,
    Constructor<
      BaseSyncOperationGeneric<ISyncOperation | ISyncOperationGuestMode | IExternalOperation>
    >
  >
> => ({
  /**
   * Standard Sync Operations
   */
  CREATE_NOTE: CreateNoteOperation,
  DELETE_NOTE: DeleteNoteOperation,
  TRASH_NOTE: TrashNoteOperation,
  RESTORE_NOTE: RestoreNoteOperation,
  UPDATE_NOTE_CONTENT_USING_DIFF: UpdateNoteContentUsingDiffOperation,
  UPDATE_NOTE_CONTENT_USING_TEMPLATE_AND_DIFF: UpdateNoteContentUsingTemplateAndDiffOperation,
  UPDATE_NOTE_CONTENT_OVERWRITING_FULL_STATE_DESTRUCTIVELY:
    UpdateNoteContentOverwritingFullStateDestructivelyOperation,
  GRANT_NOTE_ACL_VIA_SPACE_ACCOUNT: GrantNoteAclViaSpaceAccountOperation,
  GRANT_NOTE_ACL_VIA_EMAIL_ADDRESS: GrantNoteAclViaEmailAddressOperation,
  UPDATE_NOTE_ACL_VIA_SPACE_ACCOUNT: UpdateNoteAclViaSpaceAccountOperation,
  UPDATE_NOTE_ACL_VIA_EMAIL_ADDRESS: UpdateNoteAclViaEmailAddressOperation,
  REVOKE_NOTE_ACL_VIA_SPACE_ACCOUNT: RevokeNoteAclViaSpaceAccountOperation,
  REVOKE_NOTE_ACL_VIA_EMAIL_ADDRESS: RevokeNoteAclViaEmailAddressOperation,
  UPDATE_NOTE_ACL_VIA_COLLECTION: UpdateNoteAclViaCollectionOperation,
  CREATE_COLLECTION: CreateCollectionOperation,
  UPDATE_COLLECTION: UpdateCollectionOperation,
  DELETE_COLLECTION: DeleteCollectionOperation,
  ADD_NOTE_TO_COLLECTION: AddNoteToCollectionOperation,
  REMOVE_NOTE_FROM_COLLECTION: RemoveNoteFromCollectionOperation,
  GRANT_COLLECTION_ACL_VIA_SPACE_ACCOUNT: GrantCollectionAclViaSpaceAccountOperation,
  GRANT_COLLECTION_ACL_VIA_EMAIL_ADDRESS: GrantCollectionAclViaEmailAddressOperation,
  UPDATE_COLLECTION_ACL_VIA_SPACE_ACCOUNT: UpdateCollectionAclViaSpaceAccountOperation,
  UPDATE_COLLECTION_ACL_VIA_EMAIL_ADDRESS: UpdateCollectionAclViaEmailAddressOperation,
  REVOKE_COLLECTION_ACL_VIA_SPACE_ACCOUNT: RevokeCollectionAclViaSpaceAccountOperation,
  REVOKE_COLLECTION_ACL_VIA_EMAIL_ADDRESS: RevokeCollectionAclViaEmailAddressOperation,
  MAKE_COLLECTION_PRIVATE: MakeCollectionPrivateOperation,
  ADD_NOTE_TO_FAVORITES: AddNoteToFavoritesOperation,
  ADD_COLLECTION_TO_FAVORITES: AddCollectionToFavoritesOperation,
  ADD_SAVED_SEARCH_TO_FAVORITES: AddSavedSearchToFavoritesOperation,
  REMOVE_NOTE_FROM_FAVORITES: RemoveNoteFromFavoritesOperation,
  REMOVE_COLLECTION_FROM_FAVORITES: RemoveCollectionFromFavoritesOperation,
  REMOVE_SAVED_SEARCH_FROM_FAVORITES: RemoveSavedSearchFromFavoritesOperation,
  UPDATE_FAVORITE_ITEM_SORT_KEY: UpdateFavoriteItemSortKeyOperation,
  MARK_NOTE_VIEWED: MarkNoteViewedOperation,
  MARK_COLLECTION_VIEWED: MarkCollectionViewedOperation,
  CREATE_CHAT_CONVERSATION: CreateChatConversationOperation,
  SUBMIT_CHAT_MESSAGE: SubmitChatMessageOperation,
  SUBMIT_GUIDED_CHAT_MESSAGE: SubmitGuidedChatMessageOperation,
  SAVE_DRAFT_NOTE: SaveDraftNoteOperation,
  SAVE_CHAT_MESSAGE: SaveChatMessageOperation,
  PROVIDE_CHAT_MESSAGE_FEEDBACK: ProvideChatMessageFeedbackOperation,
  PROVIDE_FEEDBACK: ProvideFeedbackOperation,
  DISMISS_SPACE_ACCOUNT_APP_CALLOUT: DismissSpaceAccountAppCalloutOperation,
  CREATE_API_KEY: CreateApiKeyOperation,
  UPDATE_API_KEY: UpdateApiKeyOperation,
  DELETE_API_KEY: DeleteApiKeyOperation,
  CREATE_TEMPLATE: CreateTemplateOperation,
  UPDATE_TEMPLATE: UpdateTemplateOperation,
  UPDATE_TEMPLATE_CONTENT_USING_DIFF: UpdateTemplateContentUsingDiffOperation,
  DELETE_TEMPLATE: DeleteTemplateOperation,
  GRANT_TEMPLATE_ACL_VIA_SPACE_ACCOUNT: GrantTemplateAclViaSpaceAccountOperation,
  GRANT_TEMPLATE_ACL_VIA_EMAIL_ADDRESS: GrantTemplateAclViaEmailAddressOperation,
  UPDATE_TEMPLATE_ACL_VIA_SPACE_ACCOUNT: UpdateTemplateAclViaSpaceAccountOperation,
  REVOKE_TEMPLATE_ACL_VIA_SPACE_ACCOUNT: RevokeTemplateAclViaSpaceAccountOperation,
  ADD_TEMPLATE_TO_COLLECTION: AddTemplateToCollectionOperation,
  UPDATE_TEMPLATE_ACL_VIA_COLLECTION: UpdateTemplateAclViaCollectionOperation,
  REMOVE_TEMPLATE_FROM_COLLECTION: RemoveTemplateFromCollectionOperation,
  CREATE_NOTE_USING_TEMPLATE_AND_DIFF: CreateNoteUsingTemplateOperation,
  MARK_TEMPLATE_VIEWED: MarkTemplateViewedOperation,
  MARK_TEMPLATE_USED: MarkTemplateUsedOperation,
  CREATE_DATA_EXPORT: CreateDataExportOperation,
  CREATE_UPLOADED_FILE_BATCH: CreateUploadedFileBatchOperation,
  CREATE_UPLOADED_FILE: CreateUploadedFileOperation,
  BATCH_CREATE_UPLOADED_FILE: BatchCreateUploadedFileOperation,
  START_PROCESSING_UPLOADED_FILE_BATCH: StartProcessingUploadedFileBatchOperation,
  CREATE_SESSION: CreateSessionOperation,
  CREATE_SESSION_EVENT: CreateSessionEventOperation,
  UPDATE_SESSION: UpdateSessionOperation,
  UPDATE_UPLOADED_FILE_BATCH: UpdateUploadedFileBatchOperation,
  CREATE_MESSAGE_SENT_SESSION_EVENT: CreateMessageSentSessionEventOperation,
  DELETE_NOTE_SOURCE: DeleteNoteSourceOperation,
  ACCEPT_NOTE_SUGGESTION: AcceptNoteSuggestionOperation,
  REJECT_NOTE_SUGGESTION: RejectNoteSuggestionOperation,
  /**
   * External Operations
   */
  [ExternalOperationKind.UPLOAD_IMAGE]: UploadImageExternalOperation,
  [ExternalOperationKind.UPLOAD_FILE]: UploadFileExternalOperation,
});
